import React from "react";

import { Navigate, useSearchParams } from "react-router-dom";

import {
  DecisionHistoryRecordV2,
  SingleDecisionEnvelope,
  useHistoryDecisionV2,
} from "src/api/decisionHistoryV2/decisionHistoryQueries";
import { Flow } from "src/api/flowTypes";
import { useFlowBySlug, useWorkspace } from "src/api/queries";
import { DecisionEnvironment } from "src/api/types";
import { LoadingView } from "src/base-components/LoadingView";
import { WorkspaceDataplane } from "src/clients/flow-api";
import { isIcebergEnabledForWorkspace } from "src/hooks/useIsIcebergEnabled";
import { ErrorPage } from "src/router/ErrorPage";
import {
  DecisionHistoryKeys,
  LeftPaneOptions,
  URLKeys,
} from "src/router/SearchParams";
import { getUrlToAuthoringPage } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

const NavigateToDecision = () => {
  const [searchParams] = useSearchParams();
  const { workspace_id, flow_slug, version_id, decision_id } =
    Object.fromEntries(searchParams.entries());
  const flow = useFlowBySlug(workspace_id, flow_slug, { shallow: true });
  const workspace = useWorkspace(workspace_id);
  const decision = useHistoryDecisionV2({
    baseUrl: workspace.data?.base_url ?? "",
    decisionId: decision_id,
    // Only fetch minimal data needed for redirect (is_sandbox check)
    // to avoid 413 errors on decisions with large node_results or linked_decisions
    includeNodeResultsData: false,
    includeLinkedDecisions: false,
    includeIntegrationDurations: false,
    iceberg: isIcebergEnabledForWorkspace(workspace.data),
  });

  return (
    <LoadingView
      queryResult={[workspace, flow, decision]}
      renderErrored={(message) => {
        return <ErrorPage message={message} />;
      }}
      renderUpdated={([workspace, flow, decision]: [
        WorkspaceDataplane,
        Flow,
        SingleDecisionEnvelope<DecisionHistoryRecordV2>,
      ]) => {
        const page = getUrlToAuthoringPage(
          workspace.organization_id,
          workspace.id,
          flow.id,
          version_id,
        );
        const params = new URLSearchParams({
          [URLKeys.DecisionId]: decision_id,
          [URLKeys.LeftPane]: LeftPaneOptions.DecisionHistory,
        });

        if (!decision.isQueryError) {
          const decisionView = decision.decision.is_sandbox
            ? DecisionEnvironment.SANDBOX
            : DecisionEnvironment.LIVE;
          params.set(DecisionHistoryKeys.DecisionHistoryView, decisionView);
        }
        return <Navigate to={`${page}?${params.toString()}`} />;
      }}
    />
  );
};

type DisplayParams = {
  type: "decision";
};

/**
 * These routes must not be changed.
 * Only add new redirects.
 * Routes itself are frozen.
 */
export const DisplayRedirects: React.FC = () => {
  const { type } = useParamsDecode<DisplayParams>();

  // eslint-disable-next-line sonarjs/no-small-switch
  switch (type) {
    case "decision":
      return <NavigateToDecision />;
    default:
      return <ErrorPage message="404: Page not found" />;
  }
};
